import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

import firebase from '../firebase';
import { ensureUserDoc } from '../services/userService';

const auth = getAuth(firebase);
const db = getFirestore(firebase);

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function signup(email: string, password: string): Promise<void> {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(db, result.user);
  }

  async function login(email: string, password: string): Promise<void> {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await ensureUserDoc(db, result.user);
  }

  function logout() {
    return signOut(auth).then(() => {});
  }

  function resetPassword(email: string) {
    return sendPasswordResetEmail(auth, email).then(() => {});
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await ensureUserDoc(db, user);
        } catch (err) {
          console.error('Failed to ensure user document:', err);
        }
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
